// Typed recurring-donation endpoint functions. Backed by
// GET /api/v1/recurring-donations/*.
//
// Per CHG-1240 plan — consumed by @generated/screens/recurring-list.ts +
// recurring-detail.ts queryFn callbacks. Spec:
// specs/tier-04-interface/recurring-donation.api.yaml.

import { apiClient } from "./client";
import {
  listPageParams,
  normalizeListResponse,
  type NormalizedList,
  type RawListEnvelope,
} from "./list-contract";
import type {
  RecurringDonation,
  RecurringChargeSummary,
  RecurringDeliveryLogEntry,
  Donation,
} from "./types";

// RDM-R08 (CHG-3100) — raw row shape of the backend /delivery-logs response.
// The route returns `recipient_email` (not the v2 `recipient`) + extra fields;
// mapped to RecurringDeliveryLogEntry below.
interface RawRecurringDeliveryRow {
  id: string;
  channel: string;
  template_slug: string;
  status: string;
  sent_at?: string | null;
  recipient_email?: string | null;
  subject?: string | null;
  error_message?: string | null;
  created_at?: string | null;
}

export interface ListRecurringDonationsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: string;
  frequency?: string;
  designation_id?: string;
}

export async function listRecurringDonations(
  params: ListRecurringDonationsParams = {},
): Promise<NormalizedList<RecurringDonation>> {
  // CHG-2916 / ITER-905655 — the /recurring-donations router
  // (src/api/app/routers/recurring_donations.py:list_recurring_donations)
  // returns the `{ items, total }` RecurringDonationPagedEnvelope and
  // reads 1-based `page` / `per_page` / `search` plus `recurring_status`
  // (NOT bare `status`). It has NO `sort`, `frequency`, or
  // `designation_id` query param, so those are no longer sent — they
  // were dropped no-ops. `status` is remapped to `recurring_status`.
  const r = await apiClient().get<RawListEnvelope<RecurringDonation>>(
    "/recurring-donations",
    {
      params: {
        ...listPageParams(params),
        recurring_status: params.status,
      },
    },
  );
  return normalizeListResponse<RecurringDonation>(r.data);
}

export async function getRecurringDonation(id: string): Promise<RecurringDonation> {
  const r = await apiClient().get<RecurringDonation>(`/recurring-donations/${id}`);
  return r.data;
}

export async function getRecurringCharges(
  id: string,
): Promise<NormalizedList<RecurringChargeSummary>> {
  // RDM-R08 (CHG-3100) — the backend /charges route returns
  // { data: DonationRead[], pagination } (NOT { items } of RecurringChargeSummary),
  // so the Charges tab read `.items` of an undefined-key envelope and always
  // rendered empty. Normalize the envelope (data -> items) and map each
  // DonationRead row to the RecurringChargeSummary shape the tab renders:
  // charged_at <- paid_at ?? created_at; donations carry no recurring
  // failure_reason, so it is null.
  const r = await apiClient().get<RawListEnvelope<Donation>>(
    `/recurring-donations/${id}/charges`,
  );
  const norm = normalizeListResponse<Donation>(r.data);
  return {
    items: norm.items.map((d) => ({
      id: d.id,
      recurring_id: id,
      donation_id: d.id,
      amount_minor_units: d.amount_minor_units,
      currency: d.currency,
      status: d.status,
      failure_reason: null,
      charged_at: d.paid_at ?? d.created_at,
    })),
    total: norm.total,
  };
}

// RDM-R08 — timeline is the real merged event stream for a recurring donation
// (charge events + stage transitions + message-delivery events). The backend
// route is GET /recurring-donations/{id}/timeline; it accepts source_type /
// from_date / to_date / channel filters ONLY. The previous `kind=schedule`
// param was a no-op the route ignores — there is NO /schedule endpoint and
// /timeline does not project a forward charge schedule, so the dead
// getRecurringSchedule helper + the unsupported `/donor` sub-resource helper
// were removed (the route never existed; calls would 404).
export interface RecurringTimelineEvent {
  id?: string;
  event_type: string;
  occurred_at: string;
  title?: string | null;
  description?: string | null;
  [key: string]: unknown;
}

export async function getRecurringTimeline(
  id: string,
): Promise<RecurringTimelineEvent[]> {
  const r = await apiClient().get<RecurringTimelineEvent[]>(
    `/recurring-donations/${id}/timeline`,
  );
  return r.data;
}

export async function getRecurringDeliveryLogs(
  id: string,
): Promise<NormalizedList<RecurringDeliveryLogEntry>> {
  // RDM-R08 (CHG-3100) — the backend /delivery-logs route returns
  // { data, pagination } with rows keyed `recipient_email` (not the v2
  // `recipient`), so the Communications tab read `.items` of an undefined-key
  // envelope and always rendered empty. Normalize the envelope + map the rows.
  const r = await apiClient().get<RawListEnvelope<RawRecurringDeliveryRow>>(
    `/recurring-donations/${id}/delivery-logs`,
  );
  const norm = normalizeListResponse<RawRecurringDeliveryRow>(r.data);
  return {
    items: norm.items.map((d) => ({
      id: d.id,
      channel: d.channel,
      template_slug: d.template_slug,
      recipient: d.recipient_email ?? "",
      status: d.status,
      sent_at: d.sent_at ?? null,
    })),
    total: norm.total,
  };
}

// ── Mutations (RDM-R08) ──────────────────────────────────────────────
// Each maps to a real POST route; the BE re-enforces the matching
// permission (pause/resume/cancel require their exact permission; reconcile
// requires recurring-donation.update). 403 → v2-api-forbidden interceptor.
// Bodies are empty (reason stays None) per RecurringActionRequest.

export async function pauseRecurringDonation(id: string): Promise<RecurringDonation> {
  const r = await apiClient().post<RecurringDonation>(`/recurring-donations/${id}/pause`);
  return r.data;
}

export async function resumeRecurringDonation(id: string): Promise<RecurringDonation> {
  const r = await apiClient().post<RecurringDonation>(`/recurring-donations/${id}/resume`);
  return r.data;
}

export async function cancelRecurringDonation(id: string): Promise<RecurringDonation> {
  const r = await apiClient().post<RecurringDonation>(`/recurring-donations/${id}/cancel`);
  return r.data;
}

// CHG-2937 — admin un-park of a needs_reconciliation recurring donation.
export async function reconcileRecurringDonation(id: string): Promise<RecurringDonation> {
  const r = await apiClient().post<RecurringDonation>(`/recurring-donations/${id}/reconcile`);
  return r.data;
}

export const recurringDonationsApi = {
  listRecurringDonations,
  getRecurringDonation,
  getRecurringCharges,
  getRecurringTimeline,
  getRecurringDeliveryLogs,
  pauseRecurringDonation,
  resumeRecurringDonation,
  cancelRecurringDonation,
  reconcileRecurringDonation,
};
